// src/pages/AudioLanguage/ListAudioLanguage.js
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import AddLanguages from "./AddLanguages";
import {
  Divider,
  Drawer,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TranslateIcon from '@mui/icons-material/Translate';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
};

const ListAudioLanguage = forwardRef(({ isSetupMode = false, chatbotId, onSetupComplete, onSetupError }, ref) => {
  const theme = useTheme();
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  const { chatbotId: paramsChatbotId } = useParams();
  let chatBotId = chatbotId ? chatbotId : paramsChatbotId;

  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reload, setReload] = useState(false);

  const handleReload = () => setReload(prev => !prev);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('audio-language', 'view');
        const modifyPerm = await hasPermission('audio-language', 'add|edit');
        const deletePerm = await hasPermission('audio-language', 'delete');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setCanModify(false);
        setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    if (!isSetupMode) {
      document.title = "Audio & Language Settings | KlonIT Web Application";
    }
    if (chatBotId || !isSetupMode) {
      fetchDocuments(chatBotId);
    }
  }, [reload, isSetupMode, chatBotId]);

  const fetchDocuments = useCallback(async (currentChatbotId) => {
    setLoading(true);
    setError(null);
    try {
      const params = currentChatbotId ? { chatbot_id: currentChatbotId } : {};
      const response = await axiosInstance.get(`/get-language-audio`, { params });
      let responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
      responseData = Array.isArray(responseData) ? responseData : [];
      setDocuments(responseData);
      setFilteredDocuments(responseData);

      if (isSetupMode && responseData.length > 0) {
        try {
          let userData = {};
          const userStr = localStorage.getItem('user');
          if (userStr) userData = JSON.parse(userStr);
          userData.audio_languages = responseData;
          if (!userData.chatbot_setup_configuration) userData.chatbot_setup_configuration = {};
          userData.chatbot_setup_configuration.is_language_set = true;
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (e) {
          if (onSetupError) onSetupError("Failed to update local session with audio languages.");
        }
      } else if (isSetupMode && responseData.length === 0) {
        try {
          let userData = {};
          const userStr = localStorage.getItem('user');
          if (userStr) userData = JSON.parse(userStr);
          if (userData.chatbot_setup_configuration) {
            userData.chatbot_setup_configuration.is_language_set = false;
          }
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (e) {
          // Handle error if necessary
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Error fetching audio configurations.";
      if (isSetupMode && onSetupError) onSetupError(errorMsg);
      else setError(errorMsg);
      setDocuments([]);
      setFilteredDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [isSetupMode, onSetupError]);

  const handleDelete = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this language?")) return;
    
    try {
      await axiosInstance.delete(`/delete-language-audio/${documentId}`);
      setError("Language deleted successfully.");
      setTimeout(() => setError(null), 3000);
      fetchDocuments(chatBotId);
    } catch (err) {
      setError(err.response?.data?.message || "Error deleting language.");
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredDocuments(
      documents.filter(doc =>
        (doc.website_lang || "").toLowerCase().includes(value) ||
        (doc.website_gender || "").toLowerCase().includes(value) ||
        (doc.created_date || "").toLowerCase().includes(value)
      )
    );
  };

  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"}`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
      },
    },
  };

  const columns = [
    { name: "Language", selector: (row) => row.website_lang || "N/A", sortable: true, wrap: true },
    { name: "Gender", selector: (row) => row.website_gender || "N/A", sortable: true },
    { name: "Default", selector: (row) => (row.default_language ? "Yes" : "No"), sortable: true },
    { name: "Created", selector: (row) => row.created_date ? new Date(row.created_date).toLocaleDateString() : "N/A", sortable: true },
    ...(canDelete ? [{
      name: 'Action',
      cell: (row) => (
        <IconButton onClick={() => handleDelete(row.id)} size="small" color="error">
          <DeleteIcon />
        </IconButton>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    }] : [])
  ];

  const toggleDrawer = (open) => setIsDrawerOpen(open);

  useImperativeHandle(ref, () => ({
    checkStepCompletion: () => {
      if (documents.length === 0) {
        const noLangMsg = "Please add at least one audio language.";
        if (isSetupMode && onSetupError) onSetupError(noLangMsg);
        else setError(noLangMsg);
        return false;
      }
      if (isSetupMode && onSetupComplete) onSetupComplete({ languagesAdded: documents.length });
      return true;
    }
  }));

  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 5 }}>
        <CircularProgress size={30} />
        <Typography ml={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 6 }}>
        <Typography variant="h6">Access Denied</Typography>
        You don't have permission to view Language settings. Please contact your administrator.
      </Alert>
    );
  }

  const LanguageManagementUI = (
   <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={isSetupMode ? 2 : 3}>
        <Typography variant="h5"
            sx={{
              textAlign: isSetupMode ? "center" : "left",
              mb: 2,
              fontWeight: 600
            }}
            color="textPrimary">
          Configure Audio Languages
        </Typography>
        {canModify && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => toggleDrawer(true)}
            size={isSetupMode ? "small" : "medium"}
          >
            Add New Language
          </Button>
        )}
      </Box>
            
                  
      {isSetupMode && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 2 }}>
          Add one or more languages and voices for your chatbot's audio responses. At least one language is required.
        </Typography>
      )}

      {!isSetupMode && (
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Search languages..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearch}
          />
        </Box>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <CircularProgress />
          <Typography sx={{ mt: 1 }}>Loading languages...</Typography>
        </Box>
      ) : filteredDocuments.length === 0 ? (
        <Typography sx={{ textAlign: 'center', mt: 2, color: 'text.secondary', p: 2 }}>
          No audio languages configured yet. Click "Add New Language" to begin.
        </Typography>
      ) : (
        <DataTable
          columns={columns}
          data={filteredDocuments}
          pagination
          highlightOnHover
          pointerOnHover
          responsive
          customStyles={customDataTableStyles}
          theme={theme.palette.mode === "dark" ? "dark" : "default"}
        />
      )}

      {error && (
        <Alert severity={error.includes("success") ? "success" : "warning"} sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </>
  );

  if (isSetupMode) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        {LanguageManagementUI}
        {canModify && (
          <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={() => toggleDrawer(false)}
            PaperProps={{
              sx: {
                width: { xs: '95%', sm: '80%', md: '500px' },
                maxWidth: '500px'
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <IconButton onClick={() => toggleDrawer(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <AddLanguages
              chatbotId={chatBotId}
              onClose={() => toggleDrawer(false)}
              setReload={handleReload}
              canModify={canModify}
            />
          </Drawer>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ fontFamily, maxWidth: '1200px', width: '100%', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
          boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
        }}>
          <TranslateIcon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <Typography sx={{
            fontFamily,
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.textPrimary,
            letterSpacing: '-0.02em',
          }}>
            Audio & Language
          </Typography>
          {documents.length > 0 && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              bgcolor: 'rgba(59,130,246,0.1)',
              px: 2,
              py: 0.5,
              borderRadius: '20px',
            }}>
              <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.accent }}>
                {documents.length} {documents.length === 1 ? 'Language' : 'Languages'}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textTertiary, mt: 1 }}>
          Configure audio and language settings for your chatbot
        </Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', boxShadow: 'none' }}>
        <Box sx={{ p: 0 }}>
          {LanguageManagementUI}
          {canModify && (
            <Drawer
              anchor="right"
              open={isDrawerOpen}
              onClose={() => toggleDrawer(false)}
              PaperProps={{ sx: { width: "500px" } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                <IconButton onClick={() => toggleDrawer(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <AddLanguages
                chatbotId={chatBotId}
                onClose={() => toggleDrawer(false)}
                setReload={handleReload}
                canModify={canModify}
              />
            </Drawer>
          )}
        </Box>
      </Paper>
    </Box>
  );
});

export default ListAudioLanguage;